import cuid from "cuid";
import { get, head } from "lodash";
import moment from "moment";
import format from "string-template";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  PROFILE_LIST,
  REGEX_EXPS,
  SALE_MODES,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";
import {
  createContractNumberValidation,
  normalizeTextField,
  stripAllSpaces,
} from "utils/helpers/validations";
import * as yup from "yup";

const validator = (
  messages,
  { fetchZipCodeCities, setCity, deltaRia, deltaStart, deltaEnd, deltaEndRia },
) => {
  const requiredString = yup
    .string()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED);
  const requiredName = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.REQUIRED)
    .matches(REGEX_EXPS.name, messages.INVALID_NAME)
    .required(messages.REQUIRED);
  const requiredTextField = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED);
  const contractNumber = createContractNumberValidation(messages, REGEX_EXPS);
  const childValidationSchema = yup.object().shape({
    identity: yup.object().shape({
      user: yup.object().shape({
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .min(
            moment().subtract(25, "year").startOf("year"),
            messages.LMF.CHILD_AGE,
          )
          .max(moment(), messages.LMF.CHILD_AGE)
          .required(messages.REQUIRED),
      }),
      socialRegime: requiredString,
    }),
  });
  return yup.object().shape({
    contract: yup.mixed().when("locked", {
      is: (locked) => locked,
      then: yup.object().shape({
        saleMode: requiredString,
        issuanceDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED),
      }),
      otherwise: yup.mixed().when("retroactive", {
        is: true,
        then: yup.object().shape({
          saleMode: requiredString,
          issuanceDate: yup
            .date()
            .typeError(messages.REQUIRED)
            .min(
              moment().startOf("month"),
              format(messages.MIN_DATE, {
                date: moment().startOf("month").format(DATE_FORMAT),
              }),
            )
            .required(messages.REQUIRED),
        }),
        otherwise: yup.mixed().when("ria", {
          is: (ria) => ria,
          then: yup.object().shape({
            saleMode: requiredString,
            issuanceDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .min(
                moment().startOf("day").add(deltaRia, "days"),
                format(messages.MIN_DATE, {
                  date: moment()
                    .startOf("day")
                    .add(deltaRia, "days")
                    .format(DATE_FORMAT),
                }),
              )
              .required(messages.REQUIRED),
          }),
          otherwise: yup.object().shape({
            saleMode: requiredString,
            issuanceDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .min(
                moment().startOf("day").add(deltaStart, "days"),
                format(messages.MIN_DATE, {
                  date: moment()
                    .startOf("day")
                    .add(deltaStart, "days")
                    .format(DATE_FORMAT),
                }),
              )
              .required(messages.REQUIRED),
          }),
        }),
      }),
    }),
    contact: yup.object().shape({
      user: yup.object().shape({
        lastname: requiredName,
        firstname: requiredName,
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .max(moment().subtract(16, "years").endOf("day"), messages.AGE_16)
          .required(messages.REQUIRED),
      }),
      fields: yup.object().shape({
        profile: requiredString,
        familySituation: yup.string().when("profile", {
          is: PROFILE_LIST.RENAULT_EMPLOYEE,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) =>
            schema.typeError(messages.REQUIRED).required(messages.REQUIRED),
        }),
        eligibleMembership: yup.mixed().when("profile", {
          is: PROFILE_LIST.RENAULT_EMPLOYEE,
          then: (schema) =>
            schema
              .oneOf([true, false], messages.REQUIRED_CHOICE)
              .required(messages.REQUIRED_CHOICE),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),
      }),
      socialRegime: yup.string().when("fields.profile", {
        is: PROFILE_LIST.RENAULT_EMPLOYEE,
        then: (schema) => schema.notRequired().nullable(),
        otherwise: (schema) =>
          schema.typeError(messages.REQUIRED).required(messages.REQUIRED),
      }),
      socioProfessionalCategory: requiredString,
    }),
    projectType: yup.mixed().when("contract.saleMode", {
      is: (saleMode) => saleMode === SALE_MODES.DISTANT,
      then: requiredString,
      otherwise: yup.mixed().nullable().notRequired(),
    }),
    terminationRequest: yup
      .mixed()
      .nullable()
      .when("ria", {
        is: (ria) => ria,
        then: yup
          .object()
          .transform((value) => (value == null ? {} : value))
          .shape({
            contractNumber,
            address: requiredTextField,
            brokerageMandate: yup
              .object()
              .transform((value) => (value == null ? {} : value))
              .shape({
                currentCompany: requiredTextField,
              }),
          }),
        otherwise: yup.object().nullable().notRequired(),
      }),
    zipCode: yup.object().when("contact.fields.profile", {
      is: PROFILE_LIST.RENAULT_EMPLOYEE,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) =>
        schema.shape({
          value: yup
            .string()
            .typeError(messages.REQUIRED)
            .test("isValid", messages.INVALID_VALUE, async function (value) {
              const isValidRegex = new RegExp(REGEX_EXPS.zipCode).test(value);
              if (!isValidRegex) return false;
              const response = await fetchZipCodeCities(value);
              setCity(
                response.length === 1
                  ? {
                      id: cuid(),
                      system: CONTACT_POINT_SYSTEM.CITY,
                      value: get(head(response), "value"),
                    }
                  : {
                      id: cuid(),
                      system: CONTACT_POINT_SYSTEM.CITY,
                      value: null,
                    },
              );
              return response.length;
            }),
        }),
    }),
    phoneNumber: yup.object().shape({
      value: yup
        .string()
        .transform(stripAllSpaces)
        .nullable()
        .test("isValidPhone", messages.INVALID_VALUE, function (value) {
          return !value ? true : /^[0][0-9]{9}$/.test(value);
        }),
    }),
    children: yup.array().of(childValidationSchema),
    hasSpouse: yup.boolean().nullable(),
    spouse: yup.object().when("hasSpouse", {
      is: (hasSpouse) => hasSpouse,
      then: yup.object().shape({
        identity: yup.object().shape({
          user: yup.object().shape({
            birthDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .max(isMajor, messages.RADIANCE.SPOUSE_AGE)
              .min(
                moment().subtract(100, "year").startOf("year"),
                messages.RADIANCE.SPOUSE_AGE,
              )
              .required(messages.REQUIRED),
          }),
          socialRegime: requiredString,
        }),
      }),
    }),
  });
};

export default validator;
